import CancelIcon from "@mui/icons-material/Cancel";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	CircularProgress,
	IconButton,
	InputAdornment,
	Modal,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import type { RightioWBSAPIModelsSPSpReportsSalesAgentScreen4Result } from "@src/utilities/apis/wbs/model";
import React, { useCallback, useEffect, useState } from "react";
import TeamLeaderFailModal from "../../BookingForm/TeamLeaderFailModal";
import { useSidebarStore } from "../useSideBarStore";
import { useFetchHoldingJobs } from "./hooks";

export type HoldingJob = RightioWBSAPIModelsSPSpReportsSalesAgentScreen4Result;

// Styled components for header and rows
const StyledTableCell = styled(TableCell)(({ theme }) => ({
	"&.MuiTableCell-head": {
		backgroundColor: theme.palette.primary.dark,
		color: theme.palette.common.white,
		fontWeight: "bold",
	},
	"&.MuiTableCell-body": {
		fontSize: 14,
	},
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
	"&:nth-of-type(odd)": {
		backgroundColor: theme.palette.action.hover,
	},
	"&:hover": {
		backgroundColor: theme.palette.action.selected,
	},
}));

type TablePaginationActionsProps = {
	count: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (
		event: React.MouseEvent<HTMLButtonElement>,
		newPage: number,
	) => void;
};

// Custom pagination controls
function TablePaginationActions(props: TablePaginationActionsProps) {
	const theme = useTheme();
	const { count, page, rowsPerPage, onPageChange } = props;

	const handleFirstPageButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		onPageChange(event, 0);
	};

	const handleBackButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		onPageChange(event, page - 1);
	};

	const handleNextButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		onPageChange(event, page + 1);
	};

	const handleLastPageButtonClick = (
		event: React.MouseEvent<HTMLButtonElement>,
	) => {
		onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 2.5 }}>
			<IconButton onClick={handleFirstPageButtonClick} disabled={page === 0}>
				{theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
			</IconButton>
			<IconButton onClick={handleBackButtonClick} disabled={page === 0}>
				{theme.direction === "rtl" ? (
					<KeyboardArrowRight />
				) : (
					<KeyboardArrowLeft />
				)}
			</IconButton>
			<IconButton
				onClick={handleNextButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
			>
				{theme.direction === "rtl" ? (
					<KeyboardArrowLeft />
				) : (
					<KeyboardArrowRight />
				)}
			</IconButton>
			<IconButton
				onClick={handleLastPageButtonClick}
				disabled={page >= Math.ceil(count / rowsPerPage) - 1}
			>
				{theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
			</IconButton>
		</Box>
	);
}

type SortField = "jobId" | "booker" | "totalHours";
type SortDirection = "asc" | "desc";

const HoldingJobs: React.FC = () => {
	const [jobs, setJobs] = useState<HoldingJob[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
	const [openFailModal, setOpenFailModal] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");
	const [sortField, setSortField] = useState<SortField>("jobId");
	const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
	const { showHoldingJobs, handleMenuClick } = useSidebarStore();

	const { fetchJobs } = useFetchHoldingJobs();

	const loadJobs = useCallback(async () => {
		setLoading(true);
		try {
			const result = await fetchJobs();
			setJobs(result);
		} catch {
			// Error loading jobs - could implement error handling here if needed
		} finally {
			setLoading(false);
		}
	}, [fetchJobs]);

	useEffect(() => {
		if (showHoldingJobs) {
			loadJobs();
		}
	}, [showHoldingJobs, loadJobs]);

	const handleCancelJob = (jobId: number) => {
		setSelectedJobId(jobId);
		setOpenFailModal(true);
	};

	const handleModalClose = () => {
		setOpenFailModal(false);
		setSelectedJobId(null);
	};

	const handleJobRemoval = (jobId: number) => {
		setJobs((prev) => prev.filter((job) => job.jobId !== jobId));
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSort = (field: SortField) => {
		if (sortField === field) {
			setSortDirection(sortDirection === "asc" ? "desc" : "asc");
		} else {
			setSortField(field);
			setSortDirection("asc");
		}
		setPage(0);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
		setPage(0);
	};

	const filteredAndSortedJobs = React.useMemo(() => {
		let filtered = jobs;

		// Apply search filter
		if (searchTerm) {
			filtered = jobs.filter(
				(job) =>
					job.jobId?.toString().includes(searchTerm) ||
					job.booker?.toLowerCase().includes(searchTerm.toLowerCase()) ||
					job.totalHours?.toString().includes(searchTerm),
			);
		}

		// Apply sorting
		filtered.sort((a, b) => {
			let aValue: string | number = "";
			let bValue: string | number = "";

			switch (sortField) {
				case "jobId":
					aValue = a.jobId ?? 0;
					bValue = b.jobId ?? 0;
					break;
				case "booker":
					aValue = a.booker ?? "";
					bValue = b.booker ?? "";
					break;
				case "totalHours":
					aValue = a.totalHours ?? 0;
					bValue = b.totalHours ?? 0;
					break;
			}

			if (typeof aValue === "string" && typeof bValue === "string") {
				return sortDirection === "asc"
					? aValue.localeCompare(bValue)
					: bValue.localeCompare(aValue);
			} else {
				return sortDirection === "asc"
					? (aValue as number) - (bValue as number)
					: (bValue as number) - (aValue as number);
			}
		});

		return filtered;
	}, [jobs, searchTerm, sortField, sortDirection]);

	return (
		<Modal
			open={showHoldingJobs}
			onClose={() => handleMenuClick("Holding Jobs")}
		>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: "40%",
					bgcolor: "background.paper",
					borderRadius: 3,
					boxShadow: 24,
					p: 4,
				}}
			>
				<Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
					Holding Jobs
				</Typography>

				<TextField
					fullWidth
					variant="outlined"
					placeholder="Search by Job ID, Booker, or Total Hours..."
					value={searchTerm}
					onChange={handleSearchChange}
					sx={{ mb: 2 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon />
							</InputAdornment>
						),
					}}
				/>

				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
						<CircularProgress />
					</Box>
				) : (
					<TableContainer component={Paper} sx={{ maxHeight: 600 }}>
						<Table stickyHeader size="small">
							<TableHead>
								<TableRow>
									<StyledTableCell>
										<TableSortLabel
											active={sortField === "jobId"}
											direction={sortField === "jobId" ? sortDirection : "asc"}
											onClick={() => handleSort("jobId")}
											sx={{ color: "inherit" }}
										>
											Job ID
										</TableSortLabel>
									</StyledTableCell>
									<StyledTableCell>
										<TableSortLabel
											active={sortField === "booker"}
											direction={sortField === "booker" ? sortDirection : "asc"}
											onClick={() => handleSort("booker")}
											sx={{ color: "inherit" }}
										>
											Booker
										</TableSortLabel>
									</StyledTableCell>
									<StyledTableCell>
										<TableSortLabel
											active={sortField === "totalHours"}
											direction={
												sortField === "totalHours" ? sortDirection : "asc"
											}
											onClick={() => handleSort("totalHours")}
											sx={{ color: "inherit" }}
										>
											Total Hours
										</TableSortLabel>
									</StyledTableCell>
									<StyledTableCell align="center">Actions</StyledTableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredAndSortedJobs
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((job) => (
										<StyledTableRow key={job.jobId}>
											<StyledTableCell>{job.jobId}</StyledTableCell>
											<StyledTableCell>{job.booker}</StyledTableCell>
											<StyledTableCell>{job.totalHours}</StyledTableCell>
											<StyledTableCell align="center">
												<IconButton
													color="error"
													onClick={() => handleCancelJob(job.jobId ?? 0)}
												>
													<CancelIcon />
												</IconButton>
											</StyledTableCell>
										</StyledTableRow>
									))}
							</TableBody>
							<TableFooter>
								<TableRow>
									<TablePagination
										rowsPerPageOptions={[5, 10, 20]}
										count={filteredAndSortedJobs.length}
										rowsPerPage={rowsPerPage}
										page={page}
										onPageChange={handleChangePage}
										onRowsPerPageChange={handleChangeRowsPerPage}
										slotProps={{
											actions: {},
										}}
										ActionsComponent={TablePaginationActions}
									/>
								</TableRow>
							</TableFooter>
						</Table>
					</TableContainer>
				)}

				{selectedJobId !== null && (
					<TeamLeaderFailModal
						open={openFailModal}
						jobId={selectedJobId}
						onClose={handleModalClose}
						onSuccess={() => {
							handleJobRemoval(selectedJobId);
							handleModalClose();
						}}
						failFeedbackReason={{ otherPhoneId: 55, otherPhoneCall: "" }}
					/>
				)}
			</Box>
		</Modal>
	);
};

export default React.memo(HoldingJobs);
